import { FC, useContext, useState, useEffect, useCallback } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { isLeft } from "fp-ts/lib/Either";
import { useForm } from "react-hook-form";
import LocalizationContextType from "../../../../../domain/providers/localization/LocalizationContextType";
import ModalsContextType from "../../../../../domain/providers/modal/ModalsContextType";
import LocalizationContext from "../../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../../providers/modal/ModalsContext";
import Validators from "../../../../utils/Validators";
import CreateShiftComponentProps from "./create_shift.component.props";
import di from "../../../../../DependencyInjection";
import ButtonComponent from "../../../../components/button/ButtonComponent";
import EntitiesConstants from "../../../../utils/EntitiesConstants";
import SearchStablishmentsUseCase, { SearchStablishmentsUseCaseName } from "../../../../../domain/use_cases/stablishment/SearchStablishmentsUseCase";
import StablishmentEntity from "../../../../../domain/entities/StablishmentEntity";
import UserEntity from "../../../../../domain/entities/UserEntity";
import ScheduleEntity, { ScheduleDay } from "../../../../../domain/entities/ScheduleEntity";
import { AutoCompleteItem } from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckProps";
import AutoCompleteWithCheckComponent from "../../../../components/autocompleteWithCheck/AutoCompleteWithCheckComponent";
import GetSchedulesByStablishmentAndDayUseCase, { GetSchedulesByStablishmentAndDayUseCaseName } from "../../../../../domain/use_cases/stablishment/GetSchedulesByStablishmentAndDayUseCase";
import CreateShiftWithScheduleUseCase, { CreateShiftWithScheduleUseCaseName } from "../../../../../domain/use_cases/shift/CreateShiftWithScheduleUseCase";
import GetAvailableUsersUseCase, { GetAvailableUsersUseCaseName } from "../../../../../domain/use_cases/user/GetAvailableUsersUseCase";

const CreateShiftComponent: FC<CreateShiftComponentProps> = ({
    onCreated,
    onUpdated,
    shift,
}) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const formInfo = useForm();
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { addToast } = useContext(ModalsContext) as ModalsContextType;
    const [loading, setLoading] = useState<boolean>(false);

    // Estados para el flujo de creación
    const [selectedDate, setSelectedDate] = useState<string>("");
    const [selectedStablishment, setSelectedStablishment] = useState<StablishmentEntity | null>(null);
    const [selectedUser, setSelectedUser] = useState<UserEntity | null>(null);
    const [availableSchedules, setAvailableSchedules] = useState<ScheduleEntity[]>([]);
    const [selectedSchedule, setSelectedSchedule] = useState<ScheduleEntity | null>(null);
    const [loadingSchedules, setLoadingSchedules] = useState(false);

    // Estados para usuarios disponibles (para el AutoComplete)
    const [availableUsers, setAvailableUsers] = useState<UserEntity[]>([]);

    // Función para obtener la fecha mínima en hora local
    const getMinDate = useCallback((): string => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Función para obtener la fecha máxima (ejemplo: 3 meses adelante)
    const getMaxDate = useCallback((): string => {
        const maxDate = new Date();
        maxDate.setMonth(maxDate.getMonth() + 3);
        const year = maxDate.getFullYear();
        const month = String(maxDate.getMonth() + 1).padStart(2, '0');
        const day = String(maxDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }, []);

    // Función para buscar establecimientos
    const _onSearchStablishments = async (query: string): Promise<AutoCompleteItem<StablishmentEntity>[]> => {
        const response = await di.get<SearchStablishmentsUseCase>(SearchStablishmentsUseCaseName).call({
            query,
            page: 1,
            itemsPerPage: 20,
        });
        if (isLeft(response)) return [];
        return response.right.data.map((item) => ({
            label: `${item.name} - ${item.address}`,
            id: item.id,
            aditionalValue: item,
        }));
    };

    // Función para buscar usuarios disponibles usando GetAvailableUsersUseCase
    const _onSearchUsers = async (query: string): Promise<AutoCompleteItem<UserEntity>[]> => {
        // Solo buscar si tenemos horario y fecha seleccionados
        if (!selectedSchedule || !selectedDate) {
            return [];
        }

        const response = await di.get<GetAvailableUsersUseCase>(GetAvailableUsersUseCaseName).call({
            query: query,
            scheduleId: selectedSchedule.id,
            date: createLocalDate(selectedDate), // Usar la función local
        });

        if (isLeft(response)) {
            return [];
        }

        return response.right.map((user) => ({
            label: `${user.name} - ${user.email || 'Sin email'}`,
            id: user.id,
            aditionalValue: user,
        }));
    };

    // Función para crear fecha local sin cambios de zona horaria
    const createLocalDate = (dateString: string): Date => {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day); // month - 1 porque los meses en JS van de 0-11
    };

    // Función para obtener el día de la semana
    const getDayOfWeek = useCallback((dateString: string): ScheduleDay => {
        const date = createLocalDate(dateString); // Usar la función local
        const dayIndex = date.getDay();
        const days = [
            ScheduleDay.domingo,
            ScheduleDay.lunes,
            ScheduleDay.martes,
            ScheduleDay.miercoles,
            ScheduleDay.jueves,
            ScheduleDay.viernes,
            ScheduleDay.sabado
        ];
        return days[dayIndex];
    }, []);

    // Función para limpiar campos dependientes
    const clearDependentFields = useCallback((level: 'schedule' | 'user') => {
        if (level === 'schedule') {
            // Limpiar horario y usuario
            setSelectedSchedule(null);
            setSelectedUser(null);
            formInfo.setValue(EntitiesConstants.ShiftEntityUser, []);
            // Reset del select de horario
            const scheduleSelect = document.querySelector('select[name="scheduleId"]') as HTMLSelectElement;
            if (scheduleSelect) scheduleSelect.value = "";
        } else if (level === 'user') {
            // Solo limpiar usuario
            setSelectedUser(null);
            formInfo.setValue(EntitiesConstants.ShiftEntityUser, []);
        }
    }, [formInfo]);

    // Función para buscar schedules - corregida para usar fecha local
    const searchSchedules = useCallback(async (stablishment: StablishmentEntity, date: string) => {
        if (!date || !stablishment) {
            setAvailableSchedules([]);
            return;
        }

        setLoadingSchedules(true);

        const dayAsDate = createLocalDate(date); // Usar la función local

        try {
            const response = await di.get<GetSchedulesByStablishmentAndDayUseCase>(GetSchedulesByStablishmentAndDayUseCaseName)
                .call(stablishment.id, dayAsDate);
            if (isLeft(response)) {
                addToast(i18n("Error al buscar horarios"), "error", undefined);
                setAvailableSchedules([]);
            } else {
                setAvailableSchedules(response.right);
            }
        } catch (error) {
            addToast(i18n("Error al buscar horarios"), "error", undefined);
            setAvailableSchedules([]);
        }

        setLoadingSchedules(false);
        // Limpiar horario y usuario cuando cambien establecimiento o fecha
        clearDependentFields('schedule');
    }, [addToast, i18n, clearDependentFields]);

    // Effect para buscar horarios cuando cambian establecimiento o fecha
    useEffect(() => {
        if (selectedStablishment && selectedDate) {
            searchSchedules(selectedStablishment, selectedDate);
        } else {
            setAvailableSchedules([]);
            clearDependentFields('schedule');
        }
    }, [selectedStablishment, selectedDate, searchSchedules, clearDependentFields]);

    // Effect para monitorear cambios en el formInfo del establecimiento y usuario
    useEffect(() => {
        const subscription = formInfo.watch((value, { name }) => {

            if (name === EntitiesConstants.ShiftEntityStablishment) {
                const stablishmentData = value[EntitiesConstants.ShiftEntityStablishment];

                if (stablishmentData && stablishmentData.length > 0) {
                    const stablishment = stablishmentData[0].aditionalValue;
                    setSelectedStablishment(stablishment);
                } else {
                    setSelectedStablishment(null);
                    setAvailableSchedules([]);
                    clearDependentFields('schedule');
                }
            }

            if (name === EntitiesConstants.ShiftEntityUser) {
                const userData = value[EntitiesConstants.ShiftEntityUser];

                if (userData && userData.length > 0) {
                    const user = userData[0].aditionalValue;
                    setSelectedUser(user);
                } else {
                    setSelectedUser(null);
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [formInfo.watch, clearDependentFields]);

    // Función para manejar cambio de fecha
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        // Limpiar horarios y usuario cuando cambie la fecha
        clearDependentFields('schedule');
    };

    // Función para manejar cambio de horario
    const handleScheduleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const scheduleId = e.target.value;
        const schedule = availableSchedules.find(s => s.id === scheduleId);
        setSelectedSchedule(schedule || null);
        // Limpiar usuario cuando cambie el horario
        clearDependentFields('user');
    };

    // Función para crear el shift - corregida
    const _handleCreateShift = async (data: any) => {
        // Validar que tengamos todos los datos necesarios
        if (!selectedStablishment) {
            addToast(i18n("Debe seleccionar un establecimiento"), "error", undefined);
            return;
        }

        if (!selectedSchedule) {
            addToast(i18n("Debe seleccionar un horario"), "error", undefined);
            return;
        }

        if (!selectedDate) {
            addToast(i18n("Debe seleccionar una fecha"), "error", undefined);
            return;
        }

        if (!selectedUser) {
            addToast(i18n("Debe seleccionar un usuario"), "error", undefined);
            return;
        }

        setLoading(true);

        try {
            const date = createLocalDate(selectedDate); // Usar la función local

            const response = await di.get<CreateShiftWithScheduleUseCase>(CreateShiftWithScheduleUseCaseName)
                .call({
                    scheduleId: selectedSchedule.id,
                    date: date,
                    userId: selectedUser.id
                });

            if (isLeft(response)) {
                addToast(i18n("Error al crear turno"), "error", undefined);
            } else {
                addToast(i18n("Turno creado exitosamente"), "success", undefined);
                onCreated?.(response.right);

                // Limpiar formulario después de crear exitosamente
                formInfo.reset();
                setSelectedDate("");
                setSelectedStablishment(null);
                setSelectedUser(null);
                setSelectedSchedule(null);
                setAvailableSchedules([]);
            }
        } catch (error) {
            console.error("Error creando turno:", error);
            addToast(i18n("Error al crear turno"), "error", undefined);
        }

        setLoading(false);
    };

    const formatTime = (time: string): string => {
        return new Date(`2000-01-01T${time}:00`).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Función para formatear fecha para mostrar - corregida
    const formatDateForDisplay = (dateString: string): string => {
        const date = createLocalDate(dateString); // Usar la función local
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    };

    return (
        <form
            className="row"
            onSubmit={handleSubmit(_handleCreateShift)}
        >
            {/* Selector de Establecimiento */}
            <div className={`form-group pt-2 col-md-12 my-2 ${errors[EntitiesConstants.ShiftEntityStablishment] && "error error_group"}`}>
                <AutoCompleteWithCheckComponent
                    multiple={false}
                    keyName={EntitiesConstants.ShiftEntityStablishment}
                    label={i18n("Establecimiento")}
                    formInfo={formInfo}
                    onSearch={_onSearchStablishments}
                />
            </div>

            {/* Selector de Fecha */}
            <div className="form-group col-md-6 my-3">
                <label className="form-label">
                    {i18n("Fecha del Turno")}
                </label>
                <input
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    disabled={loading}
                    min={getMinDate()}
                    max={getMaxDate()}
                    {...register("shiftDate", Validators({ required: true }))}
                    onChange={handleDateChange}
                />
                <ErrorMessage
                    as="aside"
                    errors={errors}
                    name="shiftDate"
                />
                <small className="text-muted">
                    {i18n("Puede crear turnos desde hoy hasta 3 meses adelante")}
                </small>
            </div>

            {/* Selector de Horario */}
            <div className="form-group col-md-6 my-3">
                <label className="form-label">
                    {i18n("Horario Disponible")}
                </label>
                {loadingSchedules ? (
                    <div className="form-control d-flex align-items-center">
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        {i18n("Buscando horarios...")}
                    </div>
                ) : (
                    <select
                        className="form-control"
                        value={selectedSchedule?.id || ""}
                        disabled={loading || availableSchedules.length === 0}
                        {...register("scheduleId", Validators({ required: true }))}
                        onChange={handleScheduleChange}
                    >
                        <option value="">{i18n("Seleccionar horario")}</option>
                        {availableSchedules.map((schedule) => (
                            <option key={schedule.id} value={schedule.id}>
                                {formatTime(schedule.openTime)} - {formatTime(schedule.closeTime)}
                            </option>
                        ))}
                    </select>
                )}
                <ErrorMessage
                    as="aside"
                    errors={errors}
                    name="scheduleId"
                />
                {!loadingSchedules && availableSchedules.length === 0 && selectedDate && selectedStablishment && (
                    <small className="text-muted">
                        {i18n("No hay horarios disponibles para este día")}
                    </small>
                )}
            </div>

            {/* Selector de Usuario con AutoComplete */}
            {selectedSchedule && (
                <div className={`form-group pt-2 col-md-12 my-2 ${errors[EntitiesConstants.ShiftEntityUser] && "error error_group"}`}>
                    <AutoCompleteWithCheckComponent
                        multiple={false}
                        keyName={EntitiesConstants.ShiftEntityUser}
                        label={i18n("Usuario Disponible")}
                        formInfo={formInfo}
                        onSearch={_onSearchUsers}
                    />
                    {!selectedSchedule && (
                        <small className="text-muted">
                            {i18n("Seleccione un horario para ver usuarios disponibles")}
                        </small>
                    )}
                </div>
            )}

            {/* Información de resumen */}
            {selectedStablishment && selectedDate && selectedSchedule && selectedUser && (
                <div className="col-12 my-3">
                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">{i18n("Resumen del Turno")}</h6>
                        </div>
                        <div className="card-body">
                            <p><strong>{i18n("Establecimiento")}:</strong> {selectedStablishment.name}</p>
                            <p><strong>{i18n("Fecha")}:</strong> {formatDateForDisplay(selectedDate)}</p>
                            <p><strong>{i18n("Horario")}:</strong> {formatTime(selectedSchedule.openTime)} - {formatTime(selectedSchedule.closeTime)}</p>
                            <p><strong>{i18n("Usuario")}:</strong> {selectedUser.name}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-100 d-flex justify-content-end my-3">
                <ButtonComponent
                    isLoading={loading}
                    icon={<span className="material-symbols-outlined">add</span>}
                    type="submit"
                    design="primaryOutline"
                    text={i18n("Crear Turno")}
                    disabled={!selectedStablishment || !selectedDate || !selectedSchedule || !selectedUser}
                />
            </div>
        </form>
    );
};

export default CreateShiftComponent;